import { useMemo, useState } from "react";
import clsx from "clsx";
import { Maximize2, X } from "lucide-react";

import { AvatarMeta, StructureContact } from "../lib/api";
import { normalizeAvatarMeta } from "../lib/avatar";

type ContactAvatarProps = {
  contact: Pick<StructureContact, "name" | "avatar_url" | "avatar_meta">;
  size?: number;
  className?: string;
  enablePreview?: boolean;
  metaOverride?: AvatarMeta | null;
};

const gradients = [
  "from-indigo-500 to-purple-500",
  "from-sky-500 to-indigo-500",
  "from-emerald-500 to-teal-500",
  "from-amber-500 to-orange-500",
  "from-rose-500 to-pink-500",
  "from-cyan-500 to-blue-500",
];

const hashName = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash << 5) - hash + name.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

export function ContactAvatar({ contact, size = 40, className, enablePreview = false, metaOverride }: ContactAvatarProps) {
  const [showPreview, setShowPreview] = useState(false);

  const meta = normalizeAvatarMeta(metaOverride ?? contact.avatar_meta ?? undefined);
  const hasAvatar = Boolean(contact.avatar_url);
  const bgClass = gradients[hashName(contact.name || "") % gradients.length];
  const initial = (contact.name || "?").trim().slice(0, 2).toUpperCase();

  const imageStyle = useMemo(
    () => ({
      objectFit: "cover" as const,
      objectPosition: `${meta.x}% ${meta.y}%`,
      transform: `scale(${meta.zoom})`,
      transformOrigin: `${meta.x}% ${meta.y}%`,
    }),
    [meta.x, meta.y, meta.zoom]
  );

  return (
    <>
      <div
        className={clsx(
          "relative rounded-full overflow-hidden border border-[var(--color-border)] bg-[var(--color-panel-2)]",
          enablePreview ? "cursor-zoom-in group" : "",
          className
        )}
        style={{ width: size, height: size }}
        role={enablePreview ? "button" : undefined}
        tabIndex={enablePreview ? 0 : undefined}
        onClick={
          enablePreview
            ? (e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowPreview(true);
              }
            : undefined
        }
        onKeyDown={(e) => {
          if (!enablePreview) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            setShowPreview(true);
          }
        }}
      >
        {hasAvatar ? (
          <img src={contact.avatar_url || ""} alt={contact.name} className="w-full h-full" style={imageStyle} />
        ) : (
          <div className={clsx("w-full h-full flex items-center justify-center text-white font-semibold", "bg-gradient-to-br", bgClass)}>
            {initial}
          </div>
        )}
        {enablePreview && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition flex items-center justify-center">
            <Maximize2 className="w-4 h-4 text-white opacity-0 group-hover:opacity-80 drop-shadow" />
          </div>
        )}
      </div>

      {enablePreview && showPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setShowPreview(false)}
        >
          <div
            className="bg-[var(--color-panel)] text-[var(--color-text)] rounded-2xl shadow-2xl overflow-hidden max-w-3xl w-full sm:w-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
              <div>
                <div className="text-sm text-[var(--color-muted)]">Avatar Vorschau</div>
                <div className="text-base font-semibold">{contact.name}</div>
              </div>
              <button
                type="button"
                className="p-2 rounded-lg hover:bg-[var(--color-panel-2)]"
                onClick={() => setShowPreview(false)}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 flex items-center justify-center bg-[var(--color-panel-2)]">
              <div className="w-full sm:w-[360px] h-[360px] rounded-full overflow-hidden border border-[var(--color-border)] bg-[var(--color-panel)]">
                {hasAvatar ? (
                  <img
                    src={contact.avatar_url || ""}
                    alt={contact.name}
                    className="w-full h-full"
                    style={imageStyle}
                  />
                ) : (
                  <div className={clsx("w-full h-full flex items-center justify-center text-white text-4xl font-bold", "bg-gradient-to-br", bgClass)}>
                    {initial}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
